import * as React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TextField,
  PrimaryButton,
  DefaultButton,
  makeStyles,
  Theme,
} from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';

const MAX_CAPTAIN_IDS = 60;

interface ValidationResult {
  totalIds: number;
  uniqueIds: string[];
  duplicates: string[];
  hasError: boolean;
  errorMessages: string[];
}

interface BulkSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSearch: (captainIds: string[]) => Promise<void>;
  isLoading: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  dialogPaper: {
    minWidth: '28rem',
    maxWidth: '38rem',
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  textarea: {
    width: '100%',
    minHeight: '250px',
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    marginBottom: 0,
    '& .MuiFormControl-root': {
      marginBottom: 0,
    },
  },
  validationContainer: {
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  validationSuccess: {
    color: theme.palette.success.main,
    fontWeight: 500,
  },
  validationError: {
    color: theme.palette.error.main,
    fontWeight: 500,
  },
  duplicatesList: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(2),
    maxHeight: '100px',
    overflowY: 'auto',
    fontFamily: 'monospace',
    fontSize: '0.875rem',
  },
}));

const BulkSearchDialog: React.FC<BulkSearchDialogProps> = ({
  open,
  onClose,
  onSearch,
  isLoading,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [textInput, setTextInput] = React.useState('');
  const [validation, setValidation] = React.useState<ValidationResult>({
    totalIds: 0,
    uniqueIds: [],
    duplicates: [],
    hasError: false,
    errorMessages: [],
  });

  const parseAndValidate = React.useCallback(
    (text: string): ValidationResult => {
      const lines = text
        .split(/[\n,;\s]+/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);

      const uniqueSet = new Set(lines);
      const uniqueIds = Array.from(uniqueSet);
      const duplicates = lines.filter((item, index) => lines.indexOf(item) !== index);
      const uniqueDuplicates = Array.from(new Set(duplicates));

      const errorMessages: string[] = [];
      let hasError = false;

      if (lines.length > MAX_CAPTAIN_IDS) {
        hasError = true;
        errorMessages.push(
          t('cohorts.bulkSearch.validation.maxExceeded', {
            count: lines.length,
            excess: lines.length - MAX_CAPTAIN_IDS,
          }),
        );
      }

      if (uniqueDuplicates.length > 0) {
        hasError = true;
        errorMessages.push(
          t('cohorts.bulkSearch.validation.duplicatesFound', { count: uniqueDuplicates.length }),
        );
      }

      return {
        totalIds: lines.length,
        uniqueIds,
        duplicates: uniqueDuplicates,
        hasError,
        errorMessages,
      };
    },
    [t],
  );

  const handleTextChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const text = e.target.value;
      setTextInput(text);
      const result = parseAndValidate(text);
      setValidation(result);
    },
    [parseAndValidate],
  );

  const handleSearch = React.useCallback(async () => {
    if (!validation.hasError && validation.uniqueIds.length > 0) {
      await onSearch(validation.uniqueIds);
    }
  }, [validation, onSearch]);

  const handleClose = React.useCallback(() => {
    setTextInput('');
    setValidation({
      totalIds: 0,
      uniqueIds: [],
      duplicates: [],
      hasError: false,
      errorMessages: [],
    });
    onClose();
  }, [onClose]);

  const canSearch = !validation.hasError && validation.uniqueIds.length > 0 && !isLoading;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <DialogTitle onClose={handleClose} closeLabel={t('global.close')}>
        {t('cohorts.bulkSearch.title')}
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {t('cohorts.bulkSearch.pasteTab.helperText')}
        </Typography>

        <div className={classes.contentContainer}>
          <TextField
            id="bulk-search-textarea"
            multiline
            rows={12}
            value={textInput}
            onChange={handleTextChange}
            placeholder={t('cohorts.bulkSearch.pasteTab.placeholder')}
            className={classes.textarea}
            disabled={isLoading}
          />
        </div>

        {(validation.totalIds > 0 || validation.hasError) && (
          <div className={classes.validationContainer}>
            {validation.uniqueIds.length > 0 && !validation.hasError && (
              <Typography variant="body2" className={classes.validationSuccess}>
                {t('cohorts.bulkSearch.validation.idsDetected', {
                  count: validation.uniqueIds.length,
                })}
              </Typography>
            )}

            {validation.errorMessages.map((message, index) => (
              <Typography key={index} variant="body2" className={classes.validationError}>
                {message}
              </Typography>
            ))}

            {validation.duplicates.length > 0 && (
              <div className={classes.duplicatesList}>
                {validation.duplicates.map((captainId) => (
                  <Typography key={captainId} variant="body2" className={classes.validationError}>
                    {t('cohorts.bulkSearch.validation.duplicatesList', { captainId })}
                  </Typography>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <DefaultButton onClick={handleClose} disabled={isLoading}>
          {t('cohorts.bulkSearch.actions.cancel')}
        </DefaultButton>
        <PrimaryButton onClick={handleSearch} disabled={!canSearch}>
          {isLoading
            ? t('cohorts.bulkSearch.actions.searching')
            : t('cohorts.bulkSearch.actions.search')}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default BulkSearchDialog;
